import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as fromUploader from '../reducers';
import { GoogleTagManagerService } from '@app/core/lib/google-tag-manager/angular-google-tag-manager.service';
import { LocalStorageService } from '@app/core/services/local-storage.service';
import { RawFileStoreService } from '@app/core/services/raw-file-store.service';
import * as fromRoot from '@app/reducers';
import { cleanDeep } from '@app/shared/helpers/clean-deep';
import { generateDropboxSendPathname, generateSendPathname } from '@app/shared/helpers/location';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { errors, SmashUploader, UploaderEvents, UploaderParameters, UploaderStatus, UploadInput } from '@smash-sdk/uploader';
import { CustomEventEmitter } from '@smash-sdk/uploader/dist/core/CustomEventEmitter';
import { UploadCompleteOutput } from '@smash-sdk/uploader/dist/interface/Output';
import { of } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { Constant } from 'src/constant';
import * as UploadActions from '../actions/upload.actions';
import { EmailDelivery, LinkDelivery } from '../../interfaces/transfer';
import { PrepareActions, WorkflowActions } from '../actions';
import { CreateDropboxTransfer, TransferFiles } from '@smash-sdk/uploader/dist/interface/Transfer';
import { getLanguageWithFallback } from '@app/shared/helpers/language';

@Injectable()
export class UploadEffects {

  smashUploader: SmashUploader;
  listeners: CustomEventEmitter[] = [];

  constructor(
    private actions$: Actions,
    private store$: Store<fromRoot.State & fromUploader.State>,
    private rawFileStoreService: RawFileStoreService,
    private gtmService: GoogleTagManagerService,
    private readonly localStorageService: LocalStorageService,
    private router: Router,
  ) { }

  upload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UploadActions.Upload),
      concatLatestFrom(() => [
        this.store$.select(fromRoot.getCurrentDomain),
        this.store$.select(fromRoot.getSelectedLanguage),
        this.store$.select(fromRoot.getCurrentRegion),
        this.store$.select(fromRoot.getToken),
        this.store$.select(fromUploader.getUploadFormMode),
        this.store$.select(fromUploader.getTransferCommonParameters),
        this.store$.select(fromUploader.getTransferLinkParameters),
        this.store$.select(fromUploader.getTransferEmailParameters),
        this.store$.select(fromUploader.getUploadStatus),
        this.store$.select(fromUploader.getDropboxPreview),
      ]),
      concatMap(([_, domain, language, region, token, uploadFormMode, commonParameters, linkParameters, emailParameters, status, dropbox]) => {

        const uploadInput: UploadInput = {
          files: this.rawFileStoreService.getFiles().map(file => ({ name: file.relativePath, file: file.file })),
          teamId: domain.team,
          language: getLanguageWithFallback(language),
          password: commonParameters.password,
          preview: commonParameters.preview,
          notification: commonParameters.notification,
          availabilityDuration: commonParameters.availabilityDuration,
          promotion: commonParameters.promotion,
          customization: commonParameters.customization,
        };

        if (uploadFormMode === 'Email') {
          uploadInput.title = emailParameters.title;
          uploadInput.description = emailParameters.description;
          uploadInput.delivery = { type: 'Email', ...emailParameters.delivery };
          uploadInput.notification = {
            ...uploadInput.notification,
            // FIX ME Configuration of the notification for the email delivery, make it cleaner
            link: { enabled: false },
            receiver: { enabled: true },
          };
        }

        if (uploadFormMode === 'Link') {
          uploadInput.title = linkParameters.title;
          uploadInput.customUrl = linkParameters.customUrl?.trim();
          uploadInput.accessTracking = linkParameters.accessTracking;
          uploadInput.delivery = { type: 'Link', ...linkParameters.delivery };
          uploadInput.notification = {
            ...uploadInput.notification,
            // FIX ME Configuration of the notification for the link delivery, make it cleaner
            sender: { enabled: false },
            receiver: { enabled: false },
          };
        }

        if (dropbox?.id) {
          (uploadInput as TransferFiles & CreateDropboxTransfer).dropboxId = dropbox.id;
          uploadInput.delivery = {
            type: 'Dropbox',
            sender: {
              name: emailParameters.delivery.sender.name,
              email: emailParameters.delivery.sender.email
            },
          };
          uploadInput.notification = {
            download: commonParameters.notification.download,
            noDownload: commonParameters.notification.noDownload,
            sender: commonParameters.notification.sender,
            receiver: commonParameters.notification.receiver,
          };
          delete uploadInput.accessTracking;
          delete uploadInput.customUrl;
        }

        const cleanedUploadInput = cleanDeep(uploadInput) as UploadInput;

        const uploadParameters: UploaderParameters = {
          region,
          token,
        } as UploaderParameters;
        if (this.smashUploader && (status === UploaderStatus.Queued || status === UploaderStatus.QueueFinished)) {
          this.store$.dispatch(UploadActions.SetUploadStatus({ status: UploaderStatus.Starting }));
          this.smashUploader.update(cleanedUploadInput).then((output) => {
            this.gtmService.pushTag({ event: Constant.eventGTM.TRANSFER_INITIATED, transfer_id: output.transfer.id, });
            this.saveDeliveryParameters(cleanedUploadInput.delivery as EmailDelivery | LinkDelivery);
            this.smashUploader.resume().then(({ status: newStatus }) => {
              if (status === UploaderStatus.Queued) {
                this.store$.dispatch(UploadActions.SetUploadStatus({ status: newStatus }));
                this.store$.dispatch(PrepareActions.DisplayModalSizePremium());
              }
            });
          }).catch((error) => {
            this.store$.dispatch(UploadActions.UploadFailure({ error }));
          });
        } else {
          this.smashUploader = new SmashUploader(uploadParameters);

          const startingListener = this.smashUploader.on(UploaderEvents.Starting, (event) => {
            this.store$.dispatch(UploadActions.SetUploadStatus({ status: UploaderStatus.Starting }));
          });

          const startedListener = this.smashUploader.on(UploaderEvents.Started, (event) => {
            this.store$.dispatch(UploadActions.SetUploadStatus({ status: UploaderStatus.Started }));
            if (dropbox?.id) {
              this.router.navigate([generateDropboxSendPathname()]);
            } else {
              this.router.navigate([generateSendPathname(language)]);
            }
          });

          const queuedListener = this.smashUploader.on(UploaderEvents.Queued, (event) => {
            this.store$.dispatch(UploadActions.SetUploadStatus({ status: UploaderStatus.Queued }));
            this.store$.dispatch(UploadActions.UploadQueued({ queue: event.data.transfer.queue, queuedUntil: event.data.transfer.queuedUntil }));
            this.store$.dispatch(PrepareActions.DisplayModalSizePremium());
          });

          const queueFinishedListener = this.smashUploader.on(UploaderEvents.QueueFinished, (event) => {
            this.store$.dispatch(UploadActions.SetUploadStatus({ status: UploaderStatus.QueueFinished }));
          });

          const finishingListener = this.smashUploader.on(UploaderEvents.Finishing, (event) => {
            this.store$.dispatch(UploadActions.SetUploadStatus({ status: UploaderStatus.Finishing }));
          });

          const finishedListener = this.smashUploader.on(UploaderEvents.Finished, (event) => {
            this.store$.dispatch(UploadActions.SetUploadStatus({ status: UploaderStatus.Finished }));
          });

          const progressListener = this.smashUploader.on(UploaderEvents.Progress, (event) => {
            this.store$.dispatch(UploadActions.SetProgress({ progress: event.data.percent }));
            this.store$.dispatch(UploadActions.SetUploadedBytes({ uploadedBytes: event.data.uploadedBytes }));
            this.store$.dispatch(UploadActions.SetRemainingSize({ remainingSize: event.data.totalBytes - event.data.uploadedBytes }));
            if (event.data.speed > 0) {
              this.store$.dispatch(UploadActions.SetUploadSpeed({ speed: event.data.speed }));
            }
          });

          const errorListener = this.smashUploader.on(UploaderEvents.Error, (event) => {
            this.store$.dispatch(UploadActions.SetUploadStatus({ status: UploaderStatus.Error }));
            if (event.data.error instanceof errors.CustomUrlAlreadyInUseError) {
              this.store$.dispatch(UploadActions.UploadFailure({
                error: {
                  name: event.data.error.name,
                  requestId: event.data.error.requestId,
                  message: event.data.error.message,
                  details: {
                    primary: event.data.error.details.primary,
                  },
                }
              }));
            } else if (event.data.error instanceof errors.FileReaderNotFoundError) {
              this.store$.dispatch(UploadActions.UploadFailure({
                error: {
                  name: event.data.error.name,
                  details: {
                    primary: event.data.error.details.primary,
                  },
                }
              }));
            } else if (event.data.error instanceof errors.FileReaderAbortError) {
              this.store$.dispatch(UploadActions.UploadFailure({
                error: {
                  name: event.data.error.name,
                  details: {
                    primary: event.data.error.details.primary,
                  },
                }
              }));
            } else if (event.data.error instanceof errors.FileReaderNotReadableError) {
              this.store$.dispatch(UploadActions.UploadFailure({
                error: {
                  name: event.data.error.name,
                  details: {
                    primary: event.data.error.details.primary,
                  },
                }
              }));
            } else if (event.data.error instanceof errors.FileReaderSecurityError) {
              this.store$.dispatch(UploadActions.UploadFailure({
                error: {
                  name: event.data.error.name,
                  details: {
                    primary: event.data.error.details.primary,
                  },
                }
              }));
            } else if (event.data.error instanceof errors.FileReaderUnknownError) {
              this.store$.dispatch(UploadActions.UploadFailure({
                error: {
                  name: event.data.error.name,
                  details: {
                    primary: event.data.error.details.primary,
                  },
                }
              }));
            } else if (event.data.error instanceof errors.UnauthorizedError) {
              this.store$.dispatch(UploadActions.UploadFailure({
                error: {
                  name: event.data.error.name,
                  requestId: event.data.error.requestId,
                  message: event.data.error.message,
                }
              }));
            } else if (event.data.error instanceof errors.UnvalidTokenError) {
              this.store$.dispatch(UploadActions.UploadFailure({
                error: {
                  name: event.data.error.name,
                }
              }));
            } else if (event.data.error instanceof errors.PasswordRequiredError) {
              this.store$.dispatch(UploadActions.UploadFailure({
                error: {
                  name: event.data.error.name,
                  requestId: event.data.error.requestId,
                  message: event.data.error.message,
                }
              }));
            } else if (event.data.error instanceof errors.InvalidDeliveryError) {
              this.store$.dispatch(UploadActions.UploadFailure({
                error: {
                  name: event.data.error.name,
                  requestId: event.data.error.requestId,
                  message: event.data.error.message,
                }
              }));
            } else if (event.data.error instanceof errors.InvalidSubscriptionError) {
              this.store$.dispatch(UploadActions.UploadFailure({
                error: {
                  name: event.data.error.name,
                  requestId: event.data.error.requestId,
                  message: event.data.error.message,
                }
              }));
            } else if (event.data.error instanceof errors.UnsupportedFileSourceError) {
              this.store$.dispatch(UploadActions.UploadFailure({
                error: {
                  name: event.data.error.name,
                }
              }));
            } else if (event.data.error instanceof errors.UnsupportedFileTypeError) {
              this.store$.dispatch(UploadActions.UploadFailure({
                error: {
                  name: event.data.error.name,
                }
              }));
            } else if (event.data.error instanceof errors.UsageExceededError) {
              this.store$.dispatch(UploadActions.UploadFailure({
                error: {
                  name: event.data.error.name,
                  requestId: event.data.error.requestId,
                  message: event.data.error.message,
                }
              }));
            } else if (event.data.error instanceof errors.NetworkError) {
              this.store$.dispatch(UploadActions.UploadFailure({
                error: {
                  name: event.data.error.name,
                }
              }));
            } else if (event.data.error instanceof errors.InvalidParameterError) {
              this.store$.dispatch(UploadActions.UploadFailure({
                error: {
                  name: event.data.error.name,
                  requestId: event.data.error.requestId,
                  message: event.data.error.message,
                }
              }));
            } else {
              this.store$.dispatch(UploadActions.UploadFailure({
                error: {
                  name: event.data.error.name,
                }
              }));
            }
          });

          this.listeners = [queuedListener, queueFinishedListener, startingListener, startedListener, progressListener, finishingListener, finishedListener, errorListener];

          this.saveDeliveryParameters(cleanedUploadInput.delivery as EmailDelivery | LinkDelivery);

          this.smashUploader.upload(cleanedUploadInput).then((output: UploadCompleteOutput) => {
            this.gtmService.pushTag({ event: Constant.eventGTM.TRANSFER_LOCKED, transfer_id: output.transfer.id, });
            this.store$.dispatch(UploadActions.UploadSuccess({ transfer: output.transfer }));
          }).catch((error) => {
            this.store$.dispatch(UploadActions.UploadFailure({ error: { name: error.name } }));
          });
        }

        return of({ type: 'NO_ACTION' });
      }),
    )
  );

  cancelUpload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UploadActions.CancelUpload),
      map(() => {
        this.store$.dispatch(WorkflowActions.updateStep({ step: 'PREPARE' }));
        this.store$.dispatch(UploadActions.SetUploadStatus({ status: UploaderStatus.Pending }));
        this.listeners.map(listener => listener.off());
        if (this.smashUploader) {
          this.smashUploader.cancel();
          this.smashUploader.off();
          this.smashUploader = null;
        }
        return UploadActions.CancelUploadSuccess();
      })
    )
  );

  resetUpload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UploadActions.ResetUpload),
      map(() => {
        this.store$.dispatch(WorkflowActions.updateStep({ step: 'LANDING' }));
        this.store$.dispatch(UploadActions.SetUploadStatus({ status: UploaderStatus.Pending }));
        this.listeners.map(listener => listener.off());
        if (this.smashUploader) {
          this.smashUploader.cancel();
          this.smashUploader.off();
          this.smashUploader = null;
        }
        this.rawFileStoreService.reset();
        return UploadActions.ResetUploadSuccess();
      })
    )
  );

  private saveDeliveryParameters(deliveryParameters: EmailDelivery | LinkDelivery) {
    this.localStorageService.setItem('lastTransferParameters', btoa(unescape(encodeURIComponent(JSON.stringify(deliveryParameters)))));
  }
}
